import axios from "axios";
import { envConfig } from "../utils/envConfig";
import { getAccountId, getAuthToken } from "../context/AuthActions";

// Set default content type and base URL for axios
axios.defaults.headers.post["Content-Type"] = "application/json";
axios.defaults.baseURL = envConfig.baseUrl;
axios.defaults.headers.common["Content-Type"] = "application/json";

// Intercepting to capture errors
axios.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error && error.response) {
      // Keep the original error object so we can access error.response.data.message
      return Promise.reject(error);
    }
    return Promise.reject(error);
  }
);

axios.interceptors.request.use(
  async (config) => {
    // Add auth token to requests
    const token = getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Add account ID header if available
    const accountId = getAccountId();
    if (accountId) {
      config.headers["x-account-id"] = accountId;
    }

    return config;
  },
  (error) => {
    // Handle errors in request creation
    return Promise.reject(error);
  }
);

// Function to set the default authorization header (legacy compatibility)
const setAuthorization = (token) => {
  if (token) {
    axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  } else {
    delete axios.defaults.headers.common["Authorization"];
  }
};

// Response Interceptor
axios.interceptors.response.use(
  (response) => {
    if (response && response.data) {
      return response;
    }
    return response;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Fetch data with GET method
const get = (url, params, config = {}) => {
  const queryString = params
    ? "?" + new URLSearchParams(params).toString()
    : "";
  return axios.get(url + queryString, config);
};

const getQuery = (url, params, config = {}) => {
  return axios.get(url, { params });
};

// Fetch a file
const getFile = (url, params) => {
  const queryString = params
    ? "?" + new URLSearchParams(params).toString()
    : "";
  return axios.get(url + queryString, { responseType: "blob" });
};

// Create a new resource with POST method
const create = (url, data) => {
  return axios.post(url, data);
};

// Update data with PATCH method
const updatePatch = (url, params = {}, data = {}, config = {}) => {
  return axios.patch(url, data, {
    ...config,
    params, // Add query parameters here
  });
};

// Update data with PUT method
const update = (url, params = {}, data = {}, config = {}) => {
  //return axios.put(url, data);
  return axios.put(url, data, {
    ...config,
    params, // Add query parameters here
  });
};

// Delete a resource
const deleteResource = (url, params = {}, data = {}, config = {}) => {
  //return axios.delete(url);
  return axios.delete(url, {
    ...config,
    params, // Query parameters should be included here
    data, // Body data (in case of DELETE, data might be required)
  });
};

// Create a resource with file upload
/*const createWithFile = (url, data) => {
  const formData = new FormData();
  /*for (const key in data) {
    formData.append(key, data[key]);
  }
  formData.append("data", JSON.stringify(data));

  return axios.post(url, formData, {
    headers: {
      ...axios.defaults.headers,
      "Content-Type": "multipart/form-data",
    },
  });
};*/
const createWithFile = (url, data, files = []) => {
  //const formData = new FormData();

  // Add the main data as a JSON string
  //formData.append("data", JSON.stringify(data));

  // Add files to FormData
  /*files.forEach((file, index) => {
    formData.append(`files[${index}]`, file);
  });*/
  // files.forEach((file) => {
  //   data.append("files", file /*.originFileObj*/); // Ensure original file object is appended
  // });

  return axios.post(url, data, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
};

const updateWithFile = (url, data, files = []) => {
  const formData = new FormData();

  // Add the main data as a JSON string
  formData.append("data", JSON.stringify(data));

  // Add files to FormData
  /*files.forEach((file, index) => {
    formData.append(`files[${index}]`, file);
  });*/
  files.forEach((file) => {
    formData.append("images", file /*.originFileObj*/); // Ensure original file object is appended
  });

  return axios.patch(url, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
};

// // Update with file upload
// const updateWithFile = (url, data) => {
//   const formData = new FormData();
//   for (const key in data) {
//     formData.append(key, data[key]);
//   }

//   return axios.patch(url, formData, {
//     headers: {
//       ...axios.defaults.headers,
//       "Content-Type": "multipart/form-data",
//     },
//   });
// };

// Add images to a resource with file upload
const addImages = (url, images) => {
  if (!Array.isArray(images) || images.length === 0) {
    throw new Error("images must be a non-empty array of files.");
  }

  const formData = new FormData();
  images.forEach((image, index) => {
    formData.append("images", image); // Use dynamic keys for multiple images
  });

  return axios.post(url, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
};

export const axiosInstance = axios;

export {
  setAuthorization,
  create,
  createWithFile,
  deleteResource,
  get,
  getFile,
  getQuery,
  update,
  updatePatch,
  updateWithFile,
  addImages,
};
